import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { lookupInviteToken, respondInviteToken, errText, PENDING_INVITE_KEY } from "../lib/teamInvites";

/** The emailed invite link: shows the team, then accept or decline (signed in with the invited address). */
export default function InvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [token] = useState(() => params.get("token") || "");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [needsCode, setNeedsCode] = useState(false);

  useEffect(() => {
    window.history.replaceState({}, document.title, "/invite");  // keep the token out of history/referrers
    if (!token) { setError("This invite link is incomplete."); return; }
    lookupInviteToken(token).then(setInfo).catch((e) => setError(errText(e, "This invite is no longer valid.")));
  }, [token]);

  const signIn = () => {
    try { sessionStorage.setItem(PENDING_INVITE_KEY, token); } catch {}
    navigate("/login");
  };

  const respond = async (accept) => {
    setBusy(true);
    try {
      const r = await respondInviteToken(token, accept, code);
      if (r?.requires_2fa) {
        setNeedsCode(true);
        setError("");
        return;
      }
      try { sessionStorage.removeItem(PENDING_INVITE_KEY); } catch {}
      setResult(r);
    } catch (e) {
      setError(errText(e, "Something went wrong. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const wrongUser = user && info && user.email?.toLowerCase() !== info.email;
  return (
    <div className="mx-auto max-w-md px-6 py-24 text-center" data-testid="invite-page">
      <h1 className="font-display text-2xl font-bold">Team invitation</h1>
      {error && <p className="mt-4 text-sm text-red-600" data-testid="invite-error">{error}</p>}
      {result && (
        <p className="mt-4 text-sm" data-testid="invite-result">
          {result.status === "accepted"
            ? <>You joined <strong>{result.team_name}</strong>. <Link className="underline" to="/workspace">Open the workspace</Link></>
            : "Invitation declined. Nothing was shared with you."}
        </p>
      )}
      {info && !result && !error && (
        <>
          <p className="mt-4 text-sm text-muted-foreground" data-testid="invite-info">
            {info.inviter_name || "A teammate"} invited <strong>{info.email}</strong> to the <strong>{info.team_name}</strong> team on Nimbus.
          </p>
          {!loading && !user && (
            <Button className="mt-8 rounded-full" onClick={signIn} data-testid="invite-sign-in">Sign in or create an account to respond</Button>
          )}
          {wrongUser && (
            <p className="mt-6 text-sm text-red-600">You're signed in as {user.email}. Sign in with {info.email} to accept this invitation.</p>
          )}
          {user && !wrongUser && needsCode && (
            <input className="mt-6 w-full rounded border px-3 py-2" value={code} onChange={(e) => setCode(e.target.value)}
                   placeholder="Authenticator code" data-testid="invite-2fa-code" />
          )}
          {user && !wrongUser && (
            <div className="mt-8 flex justify-center gap-3">
              <Button className="rounded-full" disabled={busy} onClick={() => respond(true)} data-testid="invite-accept">Accept</Button>
              <Button variant="outline" className="rounded-full" disabled={busy} onClick={() => respond(false)} data-testid="invite-decline">Decline</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
